export function formatEur(cents: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100)
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date))
}

export function formatDateShort(date: string | Date): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(date))
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${h}h`
}

export const MISSION_TYPE_LABEL: Record<string, string> = {
  EVENT: 'Événement',
  TRAINING: 'Formation',
  OTHER: 'Autre',
  ADS: 'Agent de sécurité',
  SSIAP: 'SSIAP',
}

export const MISSION_TYPE_CONFIG: Record<string, { label: string; icon: string; color: string; bg: string; border: string }> = {
  EVENT:    { label: 'Événement',          icon: '🎪', color: 'text-orange-300', bg: 'bg-orange-500/10', border: 'border-orange-500/25' },
  TRAINING: { label: 'Formation',          icon: '🎓', color: 'text-[#8B9AFF]', bg: 'bg-accent/10',     border: 'border-accent/25'     },
  OTHER:    { label: 'Autre',              icon: '🔧', color: 'text-text-muted', bg: 'bg-white/[0.06]',  border: 'border-white/[0.10]'  },
  ADS:      { label: 'Agent de sécurité',  icon: '🛡️', color: 'text-blue-300',  bg: 'bg-blue-500/10',   border: 'border-blue-500/25'   },
  SSIAP:    { label: 'SSIAP',             icon: '🛡️', color: 'text-blue-300',  bg: 'bg-blue-500/10',   border: 'border-blue-500/25'   },
}

export const MISSION_STATUS_LABEL: Record<string, string> = {
  OPEN: 'Ouverte',
  ASSIGNED: 'Assignée',
  IN_PROGRESS: 'En cours',
  COMPLETED: 'Terminée',
  CANCELLED: 'Annulée',
}

export const MISSION_STATUS_COLOR: Record<string, string> = {
  OPEN: 'bg-accent/10 border-accent/25 text-[#8B9AFF]',
  ASSIGNED: 'bg-success/10 border-success/25 text-success',
  IN_PROGRESS: 'bg-warning/10 border-warning/25 text-warning',
  COMPLETED: 'bg-white/[0.08] border-white/[0.15] text-text-secondary',
  CANCELLED: 'bg-danger/10 border-danger/25 text-danger',
}

// Les spécialités sont pilotées depuis l'admin (SpecialtyConfig) — clé libre, plus d'union figée
export type ProviderSpecialtyKey = string

export interface SpecialtyInfo {
  label: string
  shortLabel: string
  icon: string
  description: string
  badgeClass: string
  popular?: boolean
}

// Référentiel historique — sert de fallback (couleurs de badge) ; la source de vérité est /public/specialty-config
export const SPECIALTY_INFO: Record<string, SpecialtyInfo> = {
  SSIAP_1: {
    label: 'Agent SSIAP 1',
    shortLabel: 'SSIAP 1',
    icon: '🔥',
    description: 'Agent de service de sécurité incendie et d\'assistance à personnes',
    badgeClass: 'bg-orange-500/15 border-orange-500/30 text-orange-300',
    popular: true,
  },
  SSIAP_2: {
    label: 'Chef d\'équipe SSIAP 2',
    shortLabel: 'SSIAP 2',
    icon: '🔥',
    description: 'Chef d\'équipe SSIAP — encadrement d\'agents SSIAP 1',
    badgeClass: 'bg-orange-500/15 border-orange-500/30 text-orange-300',
  },
  SSIAP_3: {
    label: 'Chef de service SSIAP 3',
    shortLabel: 'SSIAP 3',
    icon: '🔥',
    description: 'Chef de service de sécurité incendie',
    badgeClass: 'bg-orange-500/15 border-orange-500/30 text-orange-300',
  },
  FORMATEUR_SST: {
    label: 'Formateur SST',
    shortLabel: 'Formateur SST',
    icon: '🎓',
    description: 'Sauveteur Secouriste du Travail — diplôme formateur (rouge) + carte SST (verte)',
    badgeClass: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300',
  },
  ADS: {
    label: 'Agent de sécurité (ADS)',
    shortLabel: 'ADS',
    icon: '🛡️',
    description: 'Agent de sécurité — carte professionnelle + SST à jour requis',
    badgeClass: 'bg-blue-500/15 border-blue-500/30 text-blue-300',
    popular: true,
  },
  FORMATION_EXTINCTEUR: {
    label: 'Formation manipulation extincteur',
    shortLabel: 'Extincteur',
    icon: '🧯',
    description: 'Formation à la manipulation des extincteurs et gestes de 1ère intervention',
    badgeClass: 'bg-red-500/15 border-red-500/30 text-red-300',
  },
  FORMATION_EVACUATION_ERP: {
    label: 'Formation évacuation ERP',
    shortLabel: 'Évacuation ERP',
    icon: '🚪',
    description: 'Formation à l\'évacuation des établissements recevant du public (ERP)',
    badgeClass: 'bg-yellow-500/15 border-yellow-500/30 text-yellow-300',
  },
}

export const SPECIALTY_OPTIONS = Object.entries(SPECIALTY_INFO).map(([value, info]) => ({
  value: value as ProviderSpecialtyKey,
  ...info,
}))
